import { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { 
  Phone, Mail, Clock, MapPin, CheckCircle, Send, Sparkles,
  Home as HomeIcon, Building2, Truck, HardHat, Brush, Warehouse,
  Star, Users, Calendar, Shield, ChevronRight
} from 'lucide-react';
import '@/App.css';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './components/ui/select';
import { BubbleBackground } from './components/BubbleBackground';
import { WaveSeparator } from './components/WaveSeparator';
import { QuoteModal } from './components/QuoteModal';
import { Navbar } from './components/Navbar';
import { useBubbleEffect } from './components/BubbleClick';
import { BeforeAfterGallery } from './components/BeforeAfterGallery';
import { PhotoGallery } from './components/PhotoGallery';
import ServicesPage from './pages/ServicesPage';
import GalleryPage from './pages/GalleryPage';
import ScrollToTop from './components/ScrollToTop';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Services data - simplified for homepage
const services = [
  { icon: HomeIcon, title: 'Routine Cleaning', description: 'Regular residential cleaning to keep your home spotless.' },
  { icon: Sparkles, title: 'Deep Cleaning', description: 'Thorough cleaning that reaches every corner.' },
  { icon: Truck, title: 'Move-In/Out Cleaning', description: 'Start fresh or leave your property spotless.' },
  { icon: Brush, title: 'Kitchen & Bathroom', description: 'Specialized cleaning for kitchens and bathrooms.' },
];

// Why choose us data
const features = [
  { icon: Star, title: '8+ Years Experience', description: 'Trusted by Kansas families since 2016' },
  { icon: Users, title: 'Family-Owned', description: 'Local business with personal touch' },
  { icon: Shield, title: 'Detail-Oriented', description: 'Meticulous attention to every surface' },
  { icon: Calendar, title: 'Reliable Scheduling', description: 'Consistent, on-time service' },
  { icon: MapPin, title: 'Kansas & Missouri', description: 'Serving the entire KC metro' },
];

// Service areas
const serviceAreas = [
  { city: 'Kansas City', state: 'MO' },
  { city: 'Overland Park', state: 'KS' },
  { city: 'Lenexa', state: 'KS' },
  { city: 'Olathe', state: 'KS' },
  { city: 'Leawood', state: 'KS' },
];

// Section component with animation
const Section = ({ children, className = '', id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  
  return (
    <motion.section
      id={id}
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

function HomePage() {
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '', phone: '', email: '', service: '', message: ''
  });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { triggerBubbles, BubbleEffects } = useBubbleEffect();

  const handleOpenQuote = (e) => {
    triggerBubbles(e);
    setTimeout(() => setIsQuoteOpen(true), 300);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://usebasin.com/f/9d834076c65d', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          form_type: 'Contact Form',
          name: contactForm.name,
          phone: contactForm.phone,
          email: contactForm.email,
          service: contactForm.service,
          message: contactForm.message
        })
      });
      
      if (response.ok) {
        setContactSubmitted(true);
        setContactForm({ name: '', phone: '', email: '', service: '', message: '' });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
    
    setIsSubmitting(false);
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* SEO Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Your Family House Cleaning LLC",
          "description": "Professional house cleaning services in Overland Park & Kansas City. Move-in, move-out, deep cleaning & routine cleaning.",
          "url": "https://familycleaningkc.com",
          "logo": "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/nctjn6no_ChatGPT%20Image%20Mar%201%2C%202026%2C%2003_19_59%20PM.png",
          "image": "https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/nctjn6no_ChatGPT%20Image%20Mar%201%2C%202026%2C%2003_19_59%20PM.png",
          "telephone": ["913-230-9208", "516-912-4341"],
          "email": "yourfamilyhousecleaningllc@gmail.com",
          "openingHours": "Mo-Fr 08:00-16:00",
          "areaServed": ["Kansas City, MO", "Overland Park, KS", "Lenexa, KS", "Olathe, KS", "Leawood, KS"],
          "serviceType": ["Residential Cleaning", "Deep Cleaning", "Move-In Cleaning", "Move-Out Cleaning", "Kitchen Cleaning", "Bathroom Cleaning"],
          "priceRange": "$$"
        })
      }} />

      <BubbleBackground />
      <BubbleEffects />
      <Navbar onOpenQuote={handleOpenQuote} />
      <QuoteModal isOpen={isQuoteOpen} onClose={() => setIsQuoteOpen(false)} />

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/hhzv32n4_3569955351552978925.jpeg"
            alt="Clean luxury home in Kansas City"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/30" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 text-sky-700 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              8+ Years of Trusted Service
            </span>
            
            <h2 className="text-2xl sm:text-3xl font-outfit font-semibold text-pink-500 mb-2">
              Your Family House Cleaning LLC
            </h2>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-outfit font-bold text-slate-800 leading-tight tracking-tight mb-6">
              House Cleaning Services in{' '}
              <span className="text-sky-600">Overland Park</span> &{' '}
              <span className="text-pink-500">Kansas City</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
              Professional cleaning you can trust. Serving Kansas & Missouri families with care, 
              attention to detail, and a commitment to your satisfaction.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleOpenQuote}
                className="rounded-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 
                           text-white px-8 py-6 text-lg font-medium shadow-xl shadow-pink-500/30 
                           transition-all duration-300 hover:shadow-2xl hover:shadow-pink-500/40 btn-shine"
                data-testid="hero-quote-btn"
              >
                Get a Free Quote
              </Button>
              <Button
                onClick={scrollToContact}
                variant="outline"
                className="rounded-full border-2 border-sky-500 text-sky-600 hover:bg-sky-50 
                           px-8 py-6 text-lg font-medium transition-all duration-300"
                data-testid="hero-contact-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
            </div>

            <div className="mt-10 flex items-center gap-6 text-slate-600">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm">Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm">Satisfaction Guaranteed</span>
              </div>
            </div>
          </motion.div>
        </div>
        
        <WaveSeparator fillColor="#f8fafc" />
      </section>

      {/* About Section */}
      <Section id="about" className="relative py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div variants={fadeInUp} className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/h8ds4dr5_707725169339763250.jpeg"
                  alt="Our cleaning team at work"
                  className="w-full h-80 md:h-96 object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-6 hidden md:block">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Star className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">8+</p>
                    <p className="text-slate-600 text-sm">Years Experience</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-outfit font-bold text-slate-800 mb-6 tracking-tight">
                Your Trusted Kansas City <span className="text-sky-600">Cleaning Partners</span>
              </h2>
              
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  For over 8 years, Your Family House Cleaning LLC has been the trusted choice for residential 
                  and commercial cleaning services across the Kansas City metropolitan area. As a family-owned 
                  business, we understand the importance of a clean, healthy home environment.
                </p>
                <p>
                  Our attention to detail sets us apart. Every surface, every corner, every room receives our 
                  meticulous care. We don't just clean—we ensure your space sparkles and feels truly refreshed.
                </p>
                <p>
                  We truly appreciate every new client and love welcoming you into the Your Family House Cleaning 
                  family. Your satisfaction is not just our goal—it's our promise.
                </p>
              </div>
              
              <div className="mt-8 flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-sky-100 text-sky-700 px-4 py-2 rounded-full">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm font-medium">Fully Insured</span>
                </div>
                <div className="flex items-center gap-2 bg-pink-100 text-pink-700 px-4 py-2 rounded-full">
                  <Users className="w-4 h-4" />
                  <span className="text-sm font-medium">Family-Owned</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        <WaveSeparator fillColor="#ffffff" />
      </Section>

      {/* Services Section - Simplified */}
      <Section id="services" className="relative py-20 md:py-28 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-outfit font-bold text-slate-800 mb-4 tracking-tight">
              Professional House Cleaning Services in{' '}
              <span className="text-sky-600">Overland Park</span> & <span className="text-pink-500">Lenexa</span>
            </h2>
            <p className="text-lg text-slate-600">
              Comprehensive cleaning solutions for homes and businesses throughout the Kansas City area.
            </p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                variants={fadeInUp}
                className="service-card bg-white border border-slate-100 rounded-2xl p-6 hover:border-sky-200 
                           hover:shadow-xl hover:shadow-sky-500/10 cursor-pointer group text-center"
                data-testid={`service-card-${index}`}
              >
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-sky-100 to-pink-100 
                                flex items-center justify-center mb-4 group-hover:from-sky-200 group-hover:to-pink-200 
                                transition-colors duration-300">
                  <service.icon className="w-8 h-8 text-sky-600" />
                </div>
                <h3 className="text-lg font-outfit font-semibold text-slate-800 mb-2">{service.title}</h3>
                <p className="text-slate-600 text-sm">{service.description}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div variants={fadeInUp} className="mt-10 text-center">
            <Link to="/services">
              <Button
                className="rounded-full bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 
                           text-white px-8 py-6 text-lg font-medium shadow-lg shadow-sky-500/30"
                data-testid="view-all-services-btn"
              >
                View All Services
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
        <WaveSeparator fillColor="#f8fafc" />
      </Section>

      {/* Before & After Section */}
      <Section id="results" className="relative py-20 md:py-28 bg-slate-50" data-testid="results-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-outfit font-bold text-slate-800 mb-4 tracking-tight">
              See the <span className="text-pink-500">Difference</span> We Make
            </h2>
            <p className="text-lg text-slate-600">
              Real results from our professional cleaning services. Drag the slider to compare before and after.
            </p>
          </motion.div>

          <BeforeAfterGallery />
        </div>
        <WaveSeparator fillColor="#ffffff" />
      </Section>

      {/* Photo Gallery Section */}
      <Section id="gallery" className="relative py-20 md:py-28 bg-white" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-outfit font-bold text-slate-800 mb-4 tracking-tight">
              Our <span className="text-sky-600">Work</span> in Action
            </h2>
            <p className="text-lg text-slate-600">
              Real homes we've cleaned across the Kansas City metro area.
            </p>
          </motion.div>

          <PhotoGallery onBubbleClick={triggerBubbles} />
        </div>
        <WaveSeparator fillColor="#f8fafc" />
      </Section>

      {/* Why Choose Us Section */}
      <Section id="why-us" className="relative py-20 md:py-28 bg-slate-50" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-outfit font-bold text-slate-800 mb-4 tracking-tight">
              Why Families Choose <span className="text-pink-500">Us</span>
            </h2>
            <p className="text-lg text-slate-600">
              Deep Cleaning, Move-In/Out, and Office Services delivered with professionalism and care.
            </p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6"
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                variants={fadeInUp}
                className="text-center p-6 bg-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-sky-500 to-pink-500 
                                flex items-center justify-center mb-4 shadow-lg">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-outfit font-semibold text-slate-800 mb-1">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
        <WaveSeparator fillColor="#ffffff" />
      </Section>

      {/* Service Areas Section */}
      <Section id="areas" className="relative py-20 md:py-28 bg-white" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-outfit font-bold text-slate-800 mb-4 tracking-tight">
              Proudly Serving <span className="text-sky-600">Kansas</span> & <span className="text-pink-500">Missouri</span>
            </h2>
            <p className="text-lg text-slate-600">
              Professional cleaning services throughout the Kansas City metropolitan area.
            </p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto"
          >
            {serviceAreas.map((area, index) => (
              <motion.div
                key={area.city}
                variants={fadeInUp}
                className="flex items-center gap-3 bg-gradient-to-r from-sky-50 to-pink-50 
                           px-6 py-4 rounded-full border border-sky-100 hover:border-sky-300 
                           transition-colors duration-300"
                data-testid={`area-${index}`}
              >
                <MapPin className="w-5 h-5 text-sky-600" />
                <span className="font-medium text-slate-700">{area.city}, {area.state}</span>
              </motion.div>
            ))}
          </motion.div>

          <motion.div variants={fadeInUp} className="mt-16 relative">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/yclah6um_5748846733002107081.jpeg"
                alt="Clean home in Kansas City - our service area"
                className="w-full h-64 md:h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent flex items-end">
                <div className="p-6 md:p-8 text-white">
                  <h3 className="text-xl md:text-2xl font-outfit font-semibold mb-2">
                    Your Local Kansas City Cleaning Experts
                  </h3>
                  <p className="text-white/80">
                    Serving residential and commercial clients across the metro area
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        <WaveSeparator fillColor="#f8fafc" />
      </Section>

      {/* Contact Section */}
      <Section id="contact" className="relative py-20 md:py-28 bg-slate-50" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-outfit font-bold text-slate-800 mb-4 tracking-tight">
              Get In Touch <span className="text-sky-600">Today</span>
            </h2>
            <p className="text-lg text-slate-600">
              Ready for a cleaner space? Contact us for your free estimate.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Contact Form */}
            <motion.div variants={fadeInUp} className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
              {contactSubmitted ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-outfit font-semibold text-slate-800 mb-3">
                    Message Received!
                  </h3>
                  <p className="text-slate-600 max-w-md mx-auto">
                    Thank you for contacting Your Family House Cleaning. We'll get back to you within 24 hours.
                  </p>
                  <Button
                    onClick={() => setContactSubmitted(false)}
                    variant="outline"
                    className="mt-6 rounded-full"
                    data-testid="contact-reset-btn"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name">Full Name *</Label>
                      <Input
                        id="contact-name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        placeholder="Your name"
                        required
                        className="rounded-xl"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-phone">Phone Number *</Label>
                      <Input
                        id="contact-phone"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        placeholder="(913) 555-0123"
                        required
                        className="rounded-xl"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Email Address *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                      className="rounded-xl"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-service">Service Needed</Label>
                    <Select
                      value={contactForm.service}
                      onValueChange={(value) => setContactForm({ ...contactForm, service: value })}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="contact-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="routine">Routine Cleaning</SelectItem>
                        <SelectItem value="deep">Deep Cleaning</SelectItem>
                        <SelectItem value="move-in">Move-In Cleaning</SelectItem>
                        <SelectItem value="move-out">Move-Out Cleaning</SelectItem>
                        <SelectItem value="office">Office Cleaning</SelectItem>
                        <SelectItem value="workspace">Workspace Cleaning</SelectItem>
                        <SelectItem value="construction">New Construction Cleaning</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-message">Message</Label>
                    <Textarea
                      id="contact-message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="Tell us about your cleaning needs..."
                      rows={4}
                      className="rounded-xl resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 
                               text-white py-6 text-base font-medium shadow-lg shadow-sky-500/30"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Send className="w-5 h-5" />
                        Send Message
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div variants={fadeInUp} className="space-y-6">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-outfit font-semibold text-lg text-slate-800 mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <a 
                    href="tel:9132309208" 
                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-sky-50 transition-colors"
                    data-testid="contact-phone-link"
                  >
                    <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                      <Phone className="w-5 h-5 text-sky-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Assistant</p>
                      <p className="font-medium text-slate-800">(913) 230-9208</p>
                    </div>
                  </a>
                  <a 
                    href="tel:5169124341" 
                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-sky-50 transition-colors"
                    data-testid="contact-phone-owner"
                  >
                    <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                      <Phone className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Owner</p>
                      <p className="font-medium text-slate-800">(516) 912-4341</p>
                    </div>
                  </a>
                  <a 
                    href="mailto:yourfamilyhousecleaningllc@gmail.com" 
                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-sky-50 transition-colors"
                    data-testid="contact-email-link"
                  >
                    <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                      <Mail className="w-5 h-5 text-sky-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium text-slate-800 text-sm md:text-base break-all">yourfamilyhousecleaningllc@gmail.com</p>
                    </div>
                  </a>
                  <div className="flex items-center gap-4 p-3">
                    <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Business Hours</p>
                      <p className="font-medium text-slate-800">Mon - Fri: 8:00 AM - 4:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-sky-500 to-pink-500 rounded-2xl shadow-xl p-6 text-white">
                <h3 className="font-outfit font-semibold text-xl mb-3">Ready for a Cleaner Home?</h3>
                <p className="text-white/90 mb-5">
                  Get your free, no-obligation quote today. We'll respond within 24 hours.
                </p>
                <Button
                  onClick={handleOpenQuote}
                  className="w-full rounded-full bg-white text-sky-600 hover:bg-slate-100 py-6 font-medium"
                  data-testid="contact-cta-btn"
                >
                  Request Free Quote
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/nctjn6no_ChatGPT%20Image%20Mar%201%2C%202026%2C%2003_19_59%20PM.png" 
                  alt="Your Family House Cleaning Logo"
                  className="h-52 w-auto"
                />
              </div>
              <p className="text-slate-400 text-sm mb-4">
                Professional residential cleaning services. Serving Kansas & Missouri with care.
              </p>
              <p className="text-slate-500 text-sm">
                Licensed & Insured | 8+ Years Experience
              </p>
            </div>

            <div>
              <h4 className="font-outfit font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#home" className="hover:text-white transition-colors">Home</a></li>
                <li><a href="#services" className="hover:text-white transition-colors">Services</a></li>
                <li><a href="#about" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-outfit font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  (913) 230-9208
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  yourfamilyhousecleaningllc@gmail.com
                </li>
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Mon - Fri: 8 AM - 4 PM
                </li>
                <li className="flex items-start gap-2 pt-2">
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <span>Serving Kansas City (MO), Overland Park, Lenexa, Olathe, Leawood (KS)</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-slate-800 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Your Family House Cleaning LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
